import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Plus, Eye, Pencil, Trash2, Copy, KeyRound, Loader2 } from "lucide-react";
import { Link, type LinkProps } from "react-router-dom";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border border-primary hover:bg-primary-hover",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        accent:
          "bg-accent text-accent-foreground border border-accent hover:bg-accent/90",
        outline:
          "border border-input bg-transparent hover:bg-muted text-card-foreground",
        secondary:
          "bg-primary-light text-primary hover:bg-primary-light/80",
        ghost: "hover:bg-muted hover:text-card-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3 text-xs",
        lg: "h-11 rounded-lg px-8",
        xl: "h-12 rounded-lg px-6 text-base",
        icon: "h-9 w-9",
        "icon-sm": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading, children, disabled, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled ?? isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>En cours...</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

// Convenience: Add button with plus icon
const ButtonAdd = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { label: string }
>(({ label, className, ...props }, ref) => (
  <Button ref={ref} className={className} {...props}>
    <Plus className="h-4 w-4" />
    {label}
  </Button>
));
ButtonAdd.displayName = "ButtonAdd";

// Table action buttons — small round icon buttons
const tableActionStyle =
  "h-7 w-7 rounded-full flex items-center justify-center transition-colors";

const ButtonView = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title="Voir"
      className={cn(tableActionStyle, "bg-primary-light hover:bg-primary-light/70", className)}
      {...props}
    >
      <Eye className="h-4 w-4 text-primary" />
    </button>
  )
);
ButtonView.displayName = "ButtonView";

const ButtonEdit = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title="Modifier"
      className={cn(tableActionStyle, "bg-green-100 hover:bg-green-200", className)}
      {...props}
    >
      <Pencil className="h-4 w-4 text-success" />
    </button>
  )
);
ButtonEdit.displayName = "ButtonEdit";

const ButtonDelete = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title="Supprimer"
      className={cn(tableActionStyle, "bg-red-100 hover:bg-red-200", className)}
      {...props}
    >
      <Trash2 className="h-4 w-4 text-destructive" />
    </button>
  )
);
ButtonDelete.displayName = "ButtonDelete";

const ButtonDuplicate = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title="Dupliquer"
      className={cn(tableActionStyle, "bg-blue-100 hover:bg-blue-200", className)}
      {...props}
    >
      <Copy className="h-3.5 w-3.5 text-blue-600" />
    </button>
  )
);
ButtonDuplicate.displayName = "ButtonDuplicate";

const ButtonSetPin = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title="Définir le code PIN"
      className={cn(tableActionStyle, "bg-amber-100 hover:bg-amber-200", className)}
      {...props}
    >
      <KeyRound className="h-3.5 w-3.5 text-amber-600" />
    </button>
  )
);
ButtonSetPin.displayName = "ButtonSetPin";

// Link variants for table actions
const ButtonEditLink: React.FC<LinkProps> = ({ className, ...rest }) => (
  <Link
    title="Modifier"
    className={cn(tableActionStyle, "bg-green-100 hover:bg-green-200", className as string)}
    {...rest}
  >
    <Pencil className="h-4 w-4 text-success" />
  </Link>
);

const ButtonViewLink: React.FC<LinkProps> = ({ className, ...rest }) => (
  <Link
    title="Voir"
    className={cn(tableActionStyle, "bg-primary-light hover:bg-primary-light/70", className as string)}
    {...rest}
  >
    <Eye className="h-4 w-4 text-primary" />
  </Link>
);

export {
  Button,
  ButtonAdd,
  ButtonView,
  ButtonEdit,
  ButtonDelete,
  ButtonDuplicate,
  ButtonSetPin,
  ButtonEditLink,
  ButtonViewLink,
  buttonVariants,
};
